<template>
  <!-- Order Creation Modal -->
  <Modal v-if="showOrderModal" v-model="showOrderModal" size="7xl" :prevent-outside-close="true">
    <template #title>
      <div class="flex items-center gap-2">
        <i class="fas fa-shopping-cart text-indigo-600"></i>
        <span>Create New Order</span>
      </div>
    </template>
    <template #content>
      <div class="p-2 sm:p-4">
        <OrderForm @order-created="handleOrderCreated" @cancel="showOrderModal = false" :embedded="true" />
      </div>
    </template>
  </Modal>

  <!-- Client Creation Modal -->
  <Modal v-if="showClientModal" v-model="showClientModal" size="4xl" :prevent-outside-close="true">
    <template #title>
      <div class="flex items-center gap-2">
        <i class="fas fa-user-plus text-indigo-600"></i>
        <span>Create New Client</span>
      </div>
    </template>
    <template #content>
      <div class="p-2 sm:p-4">
        <ClientForm @submit="handleClientSubmit" @cancel="showClientModal = false" />
      </div>
    </template>
  </Modal>

  <!-- Payment Modal -->
  <Modal v-if="showPaymentModal" v-model="showPaymentModal" size="5xl" :prevent-outside-close="true">
    <template #title>
      <div class="flex items-center gap-2">
        <i class="fas fa-money-bill-wave text-blue-600"></i>
        <span>Add Payment</span>
      </div>
    </template>
    <template #content>
      <div class="p-2 sm:p-4">
        <PaymentForm @cancel="showPaymentModal = false" @submit="handlePaymentSubmit" />
      </div>
    </template>
  </Modal>

  <!-- Expense Modal -->
  <Modal v-if="showExpenseModal" v-model="showExpenseModal" size="7xl" :prevent-outside-close="true">
    <template #title>
      <div class="flex items-center gap-2">
        <i class="fas fa-receipt text-orange-600"></i>
        <span>Create New Expense</span>
      </div>
    </template>
    <template #content>
      <div class="p-2 sm:p-4">
        <ExpenseFormWithItems @submit="handleExpenseSubmit" @cancel="showExpenseModal = false" />
      </div>
    </template>
  </Modal>

  <!-- Client Selector Modal for Quotation -->
  <Modal v-model="showClientSelector" size="5xl" :prevent-outside-close="true">
    <template #title>
      <div class="flex items-center gap-2">
        <i class="fas fa-user text-blue-600"></i>
        <span>Select Client for Quotation</span>
      </div>
    </template>
    <template #content>
      <div class="space-y-4 p-2 sm:p-4">
        <!-- Search -->
        <div>
          <input type="text" v-model="clientSearch" placeholder="Search clients..."
            class="w-full border-surface-300 dark:border-surface-600 bg-surface-50 dark:bg-surface-800 text-surface-900 dark:text-surface-100 rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 transition-colors placeholder-surface-500 dark:placeholder-surface-400"
            @input="debouncedFetchClients" />
        </div>

        <!-- Mobile: Card Layout -->
        <div class="block sm:hidden space-y-3">
          <template v-if="loadingClients">
            <div class="text-center text-sm text-surface-600 dark:text-surface-400 py-8">
              Loading clients...
            </div>
          </template>
          <template v-else-if="clients.length === 0">
            <div class="text-center text-sm text-surface-600 dark:text-surface-400 py-8">
              No clients found
            </div>
          </template>
          <template v-else>
            <div v-for="client in clients" :key="client.id"
              class="bg-surface-50 dark:bg-surface-800 rounded-lg border border-surface-300 dark:border-surface-600 p-4">
              <div class="space-y-2">
                <div class="font-medium text-surface-900 dark:text-surface-100">
                  {{ client.company_name || `${client.first_name} ${client.last_name}` }}
                </div>
                <div class="text-sm text-surface-600 dark:text-surface-400">
                  <div v-if="client.contact_number || client.phone">
                    <i class="fas fa-phone mr-1"></i>
                    {{ client.contact_number || client.phone }}
                  </div>
                  <div v-if="client.email">
                    <i class="fas fa-envelope mr-1"></i>
                    {{ client.email }}
                  </div>
                </div>
                <div class="pt-2">
                  <button @click="selectClient(client)"
                    class="w-full px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
                    Select Client
                  </button>
                </div>
              </div>
            </div>
          </template>
        </div>

        <!-- Desktop: Table Layout -->
        <div class="hidden sm:block bg-surface-50 dark:bg-surface-800 rounded-lg border border-surface-300 dark:border-surface-600 overflow-hidden">
          <div class="overflow-x-auto max-h-96">
            <table class="min-w-full divide-y divide-surface-300 dark:divide-surface-600">
              <thead class="bg-surface-100 dark:bg-surface-700 sticky top-0">
                <tr>
                  <th class="px-3 py-2 text-left text-xs font-medium text-surface-600 dark:text-surface-400 uppercase">Name</th>
                  <th class="px-3 py-2 text-left text-xs font-medium text-surface-600 dark:text-surface-400 uppercase">Contact</th>
                  <th class="px-3 py-2 text-left text-xs font-medium text-surface-600 dark:text-surface-400 uppercase">Email</th>
                  <th class="px-3 py-2 text-center text-xs font-medium text-surface-600 dark:text-surface-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody class="bg-surface-50 dark:bg-surface-800 divide-y divide-surface-300 dark:divide-surface-600">
                <template v-if="loadingClients">
                  <tr>
                    <td colspan="4" class="px-3 py-3 text-center text-sm text-surface-600 dark:text-surface-400">
                      Loading clients...
                    </td>
                  </tr>
                </template>
                <template v-else-if="clients.length === 0">
                  <tr>
                    <td colspan="4" class="px-3 py-3 text-center text-sm text-surface-600 dark:text-surface-400">
                      No clients found
                    </td>
                  </tr>
                </template>
                <template v-else>
                  <tr v-for="client in clients" :key="client.id"
                    class="hover:bg-surface-100 dark:hover:bg-surface-700 transition-colors">
                    <td class="px-3 py-2 text-sm text-surface-900 dark:text-surface-100">
                      {{ (client.display_name && client.display_name.trim()) || (client.company_name && client.company_name.trim()) || `${client.first_name || ''} ${client.last_name || ''}`.trim() || 'Unknown Client' }}
                    </td>
                    <td class="px-3 py-2 text-sm text-surface-600 dark:text-surface-400">{{ client.contact_number || client.phone || '-' }}</td>
                    <td class="px-3 py-2 text-sm text-surface-600 dark:text-surface-400">{{ client.email || '-' }}</td>
                    <td class="px-3 py-2 text-center">
                      <button @click="selectClient(client)"
                        class="px-2 py-1 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors">
                        Select
                      </button>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="flex justify-end">
        <button @click="showClientSelector = false"
          class="px-4 py-2 bg-surface-200 dark:bg-surface-700 text-surface-700 dark:text-surface-300 rounded-md hover:bg-surface-300 dark:hover:bg-surface-600 focus:outline-none focus:ring-2 focus:ring-surface-500">
          Cancel
        </button>
      </div>
    </template>
  </Modal>

  <!-- Quotation Create Modal -->
  <QuotationCreateModal v-if="selectedClient && showQuotationModal" v-model="showQuotationModal"
    :client="selectedClient" @created="handleQuotationCreated" />

  <!-- Tools Modal -->
  <Modal v-if="showToolsModal" v-model="showToolsModal" size="4xl">
    <template #title>
      <div class="flex items-center gap-2">
        <i class="fas fa-tools text-purple-600"></i>
        <span>Sales Tools</span>
      </div>
    </template>
    <template #content>
      <div class="p-4 space-y-6">
        <!-- Tool Tabs -->
        <div class="flex space-x-1 bg-gray-100 dark:bg-dark-700 rounded-lg p-1">
          <button v-for="tool in tools" :key="tool.id" @click="activeTool = tool.id" :class="[
            'flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors',
            activeTool === tool.id
              ? 'bg-white dark:bg-dark-800 text-gray-900 dark:text-dark-100 shadow-sm'
              : 'text-gray-600 dark:text-dark-400 hover:text-gray-900 dark:hover:text-dark-100'
          ]">
            <i :class="tool.icon" class="mr-2"></i>
            {{ tool.name }}
          </button>
        </div>

        <!-- Measurement Converter -->
        <div v-if="activeTool === 'converter'" class="space-y-4">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-dark-100">Measurement Converter</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-dark-300 mb-2">From</label>
              <div class="flex gap-2">
                <input v-model="converterInput.value" type="number" step="0.01"
                  class="flex-1 rounded-md border-gray-300 dark:border-dark-600 bg-white dark:bg-dark-800 text-gray-900 dark:text-dark-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Enter value" />
                <select v-model="converterInput.unit"
                  class="rounded-md border-gray-300 dark:border-dark-600 bg-white dark:bg-dark-800 text-gray-900 dark:text-dark-100 shadow-sm focus:border-blue-500 focus:ring-blue-500">
                  <option value="mm">Millimeters (mm)</option>
                  <option value="cm">Centimeters (cm)</option>
                  <option value="m">Meters (m)</option>
                  <option value="in">Inches (in)</option>
                  <option value="ft">Feet (ft)</option>
                </select>
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-dark-300 mb-2">To</label>
              <div class="flex gap-2">
                <input :value="convertedValue" type="number" readonly
                  class="flex-1 rounded-md border-gray-300 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-dark-100 shadow-sm"
                  placeholder="Converted value" />
                <select v-model="converterOutput.unit"
                  class="rounded-md border-gray-300 dark:border-dark-600 bg-white dark:bg-dark-800 text-gray-900 dark:text-dark-100 shadow-sm focus:border-blue-500 focus:ring-blue-500">
                  <option value="mm">Millimeters (mm)</option>
                  <option value="cm">Centimeters (cm)</option>
                  <option value="m">Meters (m)</option>
                  <option value="in">Inches (in)</option>
                  <option value="ft">Feet (ft)</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <!-- Product Price Calculator -->
        <div v-if="activeTool === 'price'" class="space-y-4">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-dark-100">Product Price Calculator</h3>

          <!-- Product Selection -->
          <div class="space-y-3">
            <label class="block text-sm font-medium text-gray-700 dark:text-dark-300">Select Product</label>
            <div class="relative">
              <input v-model="productSearch" @input="debouncedProductSearch" type="text"
                class="w-full rounded-md border-gray-300 dark:border-dark-600 bg-white dark:bg-dark-800 text-gray-900 dark:text-dark-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Search products with dimensions..." />
              <div v-if="loadingProducts" class="absolute right-3 top-3">
                <i class="fas fa-spinner fa-spin text-gray-400"></i>
              </div>
            </div>

            <!-- Product List -->
            <div v-if="productsWithDimensions.length > 0"
              class="max-h-40 overflow-y-auto border border-gray-200 dark:border-dark-600 rounded-md">
              <div v-for="product in productsWithDimensions" :key="product.id" @click="selectProduct(product)"
                class="p-3 hover:bg-gray-50 dark:hover:bg-dark-700 cursor-pointer border-b border-gray-100 dark:border-dark-600 last:border-b-0">
                <div class="flex justify-between items-start">
                  <div>
                    <div class="font-medium text-gray-900 dark:text-dark-100">{{ product.name }}</div>
                    <div class="text-sm text-gray-500 dark:text-dark-400">
                      ₱{{ parseFloat(product.base_price || 0).toFixed(2) }} per {{ product.dimension_unit || 'sqm' }}
                    </div>
                  </div>
                  <div class="text-xs text-gray-400 dark:text-dark-500">
                    {{ product.unit_of_measure || 'unit' }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Selected Product Display -->
            <div v-if="priceCalculator.selectedProduct" class="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
              <div class="flex items-center justify-between">
                <div>
                  <div class="font-medium text-blue-900 dark:text-blue-100">{{ priceCalculator.selectedProduct.name }}
                  </div>
                  <div class="text-sm text-blue-700 dark:text-blue-300">
                    ₱{{ priceCalculator.basePrice.toFixed(2) }} per {{ priceCalculator.unit }}
                  </div>
                </div>
                <button @click="priceCalculator.selectedProduct = null; priceCalculator.basePrice = 0"
                  class="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-dark-300 mb-2">Width</label>
              <div class="flex gap-2">
                <input v-model="priceCalculator.width" type="number" step="0.01"
                  class="flex-1 rounded-md border-gray-300 dark:border-dark-600 bg-white dark:bg-dark-800 text-gray-900 dark:text-dark-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Width" />
                <select v-model="priceCalculator.widthUnit"
                  class="rounded-md border-gray-300 dark:border-dark-600 bg-white dark:bg-dark-800 text-gray-900 dark:text-dark-100 shadow-sm focus:border-blue-500 focus:ring-blue-500">
                  <option value="mm">mm</option>
                  <option value="cm">cm</option>
                  <option value="m">m</option>
                  <option value="in">in</option>
                  <option value="ft">ft</option>
                </select>
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-dark-300 mb-2">Height</label>
              <div class="flex gap-2">
                <input v-model="priceCalculator.height" type="number" step="0.01"
                  class="flex-1 rounded-md border-gray-300 dark:border-dark-600 bg-white dark:bg-dark-800 text-gray-900 dark:text-dark-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Height" />
                <select v-model="priceCalculator.heightUnit"
                  class="rounded-md border-gray-300 dark:border-dark-600 bg-white dark:bg-dark-800 text-gray-900 dark:text-dark-100 shadow-sm focus:border-blue-500 focus:ring-blue-500">
                  <option value="mm">mm</option>
                  <option value="cm">cm</option>
                  <option value="m">m</option>
                  <option value="in">in</option>
                  <option value="ft">ft</option>
                </select>
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-dark-300 mb-2">Base Price (₱)</label>
              <input v-model="priceCalculator.basePrice" type="number" step="0.01"
                class="w-full rounded-md border-gray-300 dark:border-dark-600 bg-white dark:bg-dark-800 text-gray-900 dark:text-dark-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Enter base price" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-dark-300 mb-2">Quantity</label>
              <input v-model="priceCalculator.quantity" type="number" step="1" min="1"
                class="w-full rounded-md border-gray-300 dark:border-dark-600 bg-white dark:bg-dark-800 text-gray-900 dark:text-dark-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Quantity" />
            </div>
          </div>
          <div class="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
            <h4 class="font-semibold text-blue-900 dark:text-blue-100 mb-2">Calculation Result</h4>
            <div class="space-y-1 text-sm">
              <div class="flex justify-between">
                <span class="text-blue-700 dark:text-blue-300">Area:</span>
                <span class="font-mono text-blue-900 dark:text-blue-100">{{ calculatedArea }} {{
                  getAreaUnitLabel(priceCalculator.unit) }}</span>
              </div>
              <div
                class="flex justify-between font-semibold text-lg border-t border-blue-200 dark:border-blue-800 pt-2">
                <span class="text-blue-900 dark:text-blue-100">Total Price:</span>
                <span class="font-mono text-blue-900 dark:text-blue-100">₱{{ baseCost.toLocaleString('en-US', {
                  minimumFractionDigits: 2, maximumFractionDigits: 2
                }) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useApi } from '@/composables/useApi';
import { useToast } from '@/composables/useToast';
import { useMasterData } from '@/composables/useMasterData';
import Modal from '@/components/common/Modal.vue';
import OrderForm from '@/components/orders/OrderForm.vue';
import ClientForm from '@/components/clients/ClientForm.vue';
import PaymentForm from '@/components/payments/PaymentForm.vue';
import ExpenseFormWithItems from '@/components/expenses/ExpenseFormWithItems.vue';
import QuotationCreateModal from '@/components/clients/QuotationCreateModal.vue';
import debounce from 'lodash/debounce';

const router = useRouter();
const route = useRoute();
const api = useApi();
const toast = useToast();
const masterData = useMasterData();

// Modal states
const showOrderModal = ref(false);
const showClientModal = ref(false);
const showPaymentModal = ref(false);
const showExpenseModal = ref(false);
const showClientSelector = ref(false);
const showQuotationModal = ref(false);
const selectedClient = ref(null);
const clients = ref([]);
const loadingClients = ref(false);
const clientSearch = ref('');

// Tools modal state
const showToolsModal = ref(false);
const activeTool = ref('converter');
const tools = ref([
  { id: 'converter', name: 'Converter', icon: 'fas fa-ruler' },
  { id: 'price', name: 'Price Calc', icon: 'fas fa-dollar-sign' }
]);

// Measurement converter
const converterInput = ref({ value: 0, unit: 'mm' });
const converterOutput = ref({ unit: 'cm' });

// Price calculator
const priceCalculator = ref({
  selectedProduct: null,
  basePrice: 0,
  unit: 'sqm',
  width: 0,
  height: 0,
  widthUnit: 'm',
  heightUnit: 'm',
  quantity: 1
});

// Products with dimensions
const productsWithDimensions = ref([]);
const loadingProducts = ref(false);
const productSearch = ref('');

// Client selector functions
const fetchClients = async () => {
  loadingClients.value = true;
  try {
    const result = await masterData.loadClients({
      search: clientSearch.value,
      limit: 100,
      useDirectApi: true
    });
    
    // Handle paginated response structure
    if (result && typeof result === 'object' && result.data && Array.isArray(result.data)) {
      clients.value = result.data;
    } else if (Array.isArray(result)) {
      clients.value = result;
    } else {
      clients.value = [];
    }
    
    console.log('Clients loaded for selector:', clients.value.length);
  } catch (error) {
    console.error('Error fetching clients:', error);
    toast.error('Failed to load clients');
  } finally {
    loadingClients.value = false;
  }
};

const debouncedFetchClients = debounce(fetchClients, 300);

const selectClient = (client) => {
  selectedClient.value = client;
  showClientSelector.value = false;
  showQuotationModal.value = true;
};

// Event handlers
const handleOpenOrderModal = () => {
  showOrderModal.value = true;
};

const handleOpenClientModal = () => {
  showClientModal.value = true;
};

const handleOpenPaymentModal = () => {
  showPaymentModal.value = true;
};

const handleOpenExpenseModal = () => {
  showExpenseModal.value = true;
};

const handleOpenQuotationModal = () => {
  showClientSelector.value = true;
  clientSearch.value = '';
  fetchClients();
};

const handleOpenToolsModal = () => {
  showToolsModal.value = true;
};

// Form submission handlers
const handleOrderCreated = () => {
  showOrderModal.value = false;
  toast.success('Order created successfully');
  // Optionally refresh the current page if it shows orders
  if (route.name === 'orders') {
    router.go(0);
  }
};

const handleClientSubmit = async (clientData) => {
  try {
    console.log('Submitting client:', clientData);
    const response = await api.post('/clients', clientData);
    toast.success('Client created successfully');
    showClientModal.value = false;
    // Optionally refresh the current page if it shows clients
    if (route.name === 'clients') {
      router.go(0);
    }
  } catch (error) {
    console.error('Error creating client:', error);
    toast.error(`Failed to create client: ${error.response?.data?.message || error.message}`);
  }
};

const handlePaymentSubmit = async (paymentData) => {
  try {
    console.log('Submitting payment:', paymentData);
    const response = await api.post('/payments', paymentData);
    
    if (response.data?.duplicate) {
      toast.warning('This payment was already recorded. No duplicate was created.', { duration: 5000 });
    } else {
      toast.success('Payment added successfully');
    }
    
    showPaymentModal.value = false;
    // Optionally refresh the current page if it shows payments
    if (route.name === 'payments') {
      router.go(0);
    }
  } catch (error) {
    console.error('Error adding payment:', error);
    toast.error(`Failed to add payment: ${error.response?.data?.message || error.message}`);
  }
};

const handleExpenseSubmit = async (expenseData) => {
  try {
    console.log('Submitting expense:', expenseData);
    const response = await api.post('/expenses', expenseData);
    
    if (response.data) {
      toast.success('Expense created successfully');
      showExpenseModal.value = false;
      // Optionally refresh the current page if it shows expenses
      if (route.name === 'expenses') {
        router.go(0);
      }
    }
  } catch (error) {
    console.error('Error creating expense:', error);
    toast.error(error.response?.data?.message || 'Failed to create expense');
  }
};

const handleQuotationCreated = () => {
  toast.success('Quotation created successfully');
  showQuotationModal.value = false;
  selectedClient.value = null;
};

// Tools functionality - Measurement Converter
const convertedValue = computed(() => {
  if (!converterInput.value.value) return 0;

  const value = parseFloat(converterInput.value.value);
  const fromUnit = converterInput.value.unit;
  const toUnit = converterOutput.value.unit;

  // Convert to millimeters first, then to target unit
  let mmValue = value;
  switch (fromUnit) {
    case 'mm': mmValue = value; break;
    case 'cm': mmValue = value * 10; break;
    case 'm': mmValue = value * 1000; break;
    case 'in': mmValue = value * 25.4; break;
    case 'ft': mmValue = value * 304.8; break;
  }

  // Convert from millimeters to target unit
  switch (toUnit) {
    case 'mm': return mmValue;
    case 'cm': return mmValue / 10;
    case 'm': return mmValue / 1000;
    case 'in': return mmValue / 25.4;
    case 'ft': return mmValue / 304.8;
    default: return mmValue;
  }
});

// Tools functionality - Price Calculator
const calculatedArea = computed(() => {
  const width = parseFloat(priceCalculator.value.width) || 0;
  const height = parseFloat(priceCalculator.value.height) || 0;
  const widthUnit = priceCalculator.value.widthUnit;
  const heightUnit = priceCalculator.value.heightUnit;
  const unit = priceCalculator.value.unit;

  // Convert to meters first
  let widthM = width;
  let heightM = height;

  // Convert width to meters
  switch (widthUnit) {
    case 'mm': widthM = width / 1000; break;
    case 'cm': widthM = width / 100; break;
    case 'm': widthM = width; break;
    case 'in': widthM = width * 0.0254; break;
    case 'ft': widthM = width * 0.3048; break;
  }

  // Convert height to meters
  switch (heightUnit) {
    case 'mm': heightM = height / 1000; break;
    case 'cm': heightM = height / 100; break;
    case 'm': heightM = height; break;
    case 'in': heightM = height * 0.0254; break;
    case 'ft': heightM = height * 0.3048; break;
  }

  const areaSqm = widthM * heightM;

  // Return area in the appropriate unit based on product pricing unit
  switch (unit) {
    case 'sqm': return areaSqm.toFixed(2);
    case 'sqft': return (areaSqm * 10.764).toFixed(2);
    case 'piece': return '1.00';
    case 'linear':
      // For linear meters, return the larger dimension
      const larger = Math.max(widthM, heightM);
      return larger.toFixed(2);
    case 'feet':
      // For products priced per foot, calculate area in square feet
      return (areaSqm * 10.764).toFixed(2);
    default: return areaSqm.toFixed(2);
  }
});

const baseCost = computed(() => {
  const basePrice = parseFloat(priceCalculator.value.basePrice) || 0;
  const area = parseFloat(calculatedArea.value) || 0;
  const quantity = parseFloat(priceCalculator.value.quantity) || 1;

  if (priceCalculator.value.unit === 'piece') {
    return basePrice * quantity;
  }

  return basePrice * area * quantity;
});

const getAreaUnitLabel = (unit) => {
  switch (unit) {
    case 'sqm': return 'sqm';
    case 'sqft': return 'sqft';
    case 'piece': return 'piece';
    case 'linear': return 'lm'; // linear meters
    case 'feet': return 'sqft'; // when product is priced per foot, show area in sqft
    default: return 'sqm';
  }
};

// Fetch products with dimensions
const fetchProductsWithDimensions = async () => {
  loadingProducts.value = true;
  try {
    const response = await api.get('/products', {
      params: {
        search: productSearch.value,
        has_dimensions: true,
        limit: 50
      }
    });

    const products = response.data?.data || response.data || [];
    productsWithDimensions.value = products.filter(p => p.dimension_unit);
  } catch (error) {
    console.error('Error fetching products:', error);
    toast.error('Failed to load products');
  } finally {
    loadingProducts.value = false;
  }
};

const debouncedProductSearch = debounce(fetchProductsWithDimensions, 300);

const selectProduct = (product) => {
  priceCalculator.value.selectedProduct = product;
  priceCalculator.value.basePrice = parseFloat(product.base_price || 0);
  priceCalculator.value.unit = product.dimension_unit || 'sqm';

  // Set default units based on product dimension unit
  const dimensionUnit = product.dimension_unit || 'sqm';
  
  if (dimensionUnit === 'feet' || dimensionUnit === 'ft') {
    priceCalculator.value.widthUnit = 'ft';
    priceCalculator.value.heightUnit = 'ft';
  } else if (dimensionUnit === 'inches' || dimensionUnit === 'in') {
    priceCalculator.value.widthUnit = 'in';
    priceCalculator.value.heightUnit = 'in';
  } else if (dimensionUnit === 'linear' || dimensionUnit === 'lm') {
    // For linear products, default to meters
    priceCalculator.value.widthUnit = 'm';
    priceCalculator.value.heightUnit = 'm';
  } else {
    // Default for sqm, sqft, etc. - use meters
    priceCalculator.value.widthUnit = 'm';
    priceCalculator.value.heightUnit = 'm';
  }
};

// Watch for active tool changes to load products when price calculator is opened
watch(activeTool, (newTool) => {
  if (newTool === 'price' && productsWithDimensions.value.length === 0) {
    fetchProductsWithDimensions();
  }
});

// Listen to global events
const handleGlobalEvent = (event) => {
  switch (event.detail?.action) {
    case 'create-order':
      handleOpenOrderModal();
      break;
    case 'create-client':
      handleOpenClientModal();
      break;
    case 'add-payment':
      handleOpenPaymentModal();
      break;
    case 'create-expense':
      handleOpenExpenseModal();
      break;
    case 'create-quotation':
      handleOpenQuotationModal();
      break;
    case 'tools':
      handleOpenToolsModal();
      break;
  }
};

onMounted(() => {
  window.addEventListener('fab-action', handleGlobalEvent);
});

onUnmounted(() => {
  window.removeEventListener('fab-action', handleGlobalEvent);
});
</script>
